import {
  clearTokens,
  getAuthHeader,
  isUsingJwt,
  needsRefresh,
  refreshAccessToken,
} from '../jwtService';
import { logger } from '../../utils/logger';
import { getValidTenantId } from '../../utils/tenantId';
import type {
  NavigationAdapter,
  TenantContextAdapter,
  TokenStorageAdapter,
} from '../../../../shared/services/baseApiClient';

const storeRedirectAfterLogin = (): void => {
  if (typeof window === 'undefined') return;

  const currentPath = `${window.location.pathname}${window.location.search}`;
  if (currentPath.startsWith('/login')) return;

  try {
    localStorage.setItem('redirectAfterLogin', currentPath);
  } catch (error) {
    logger.debug('Failed to save redirect path to localStorage', { error });
  }
};

const redirectToLogin = (): void => {
  if (typeof window === 'undefined') return;

  if (`${window.location.pathname}${window.location.search}`.startsWith('/login')) {
    return;
  }

  storeRedirectAfterLogin();
  try {
    window.location.assign('/login');
  } catch (error) {
    logger.debug('Navigation to /login blocked (JSDOM or restricted context)', { error });
  }
};

const handleUnauthorized = (): void => {
  try {
    localStorage.removeItem('user');
  } catch (error) {
    logger.debug('Failed to remove user from localStorage during forced logout', { error });
  }

  redirectToLogin();
};

export const browserTokenStorageAdapter: TokenStorageAdapter = {
  getAuthHeader,
  needsRefresh,
  refreshAccessToken,
  clearAuth: clearTokens,
  isUsingJwt,
  /**
   * Returns true when a user record exists in localStorage (set on login,
   * removed on logout / forced re-auth).  Used by the API client to detect
   * the "token cleared after failed refresh" silent broken state.
   */
  shouldBeAuthenticated: (): boolean => {
    try {
      return !!localStorage.getItem('user');
    } catch {
      return false;
    }
  },
};

export const browserTenantContextAdapter: TenantContextAdapter = {
  getTenantId: getValidTenantId,
  /**
   * Removes all tenant-related keys from localStorage so that the next
   * request omits X-Tenant-ID and the backend falls back to its
   * membership-lookup path (step 3 in _resolve_tenant_for_user).
   * Called automatically by the API client when a stale X-Tenant-ID
   * causes a 403 Forbidden response.
   */
  clearTenantId: (): void => {
    try {
      localStorage.removeItem('tenantId');
      localStorage.removeItem('tenantName');
      localStorage.removeItem('tenantSlug');
      // Also clear the legacy 'currentTenantId' key — old code may have written
      // it to localStorage.  getValidTenantId() previously fell back to reading
      // this key, so any stale value here would survive a clearTenantId() call
      // and get re-injected as X-Tenant-ID in the retry request.
      localStorage.removeItem('currentTenantId');
      sessionStorage.removeItem('currentTenantId');
    } catch {
      // best-effort; may fail in private-browsing or sandboxed iframes
    }
  },
};

export const browserNavigationAdapter: NavigationAdapter = {
  handleAuthFailure: handleUnauthorized,
  handleAuthRefreshFailure: handleUnauthorized,
};

export const browserApiLogger = logger;
